import { useEffect, useState } from "react";
import {
  ArrowUpRight,
  BookOpen,
  Feather,
  Library,
  Share2,
} from "lucide-react";
import { LINKS, IMAGES } from "@/lib/constants";
import ShareModal from "./ShareModal";

const buildBookShareUrl = (slug) => {
  // Use the current origin so share links point to whichever domain
  // the site is being served from (production, preview, or local).
  const base = typeof window !== "undefined" ? window.location.origin : "";
  return `${base}/api/share/book/${slug}`;
};

// Local fallback used until /api/books resolves so the section never
// flashes "empty" on first paint.
const FALLBACK_BOOK = {
  slug: "rich-dad-poor-dad",
  title: "Rich Dad Poor Dad",
  author: "Robert T. Kiyosaki",
  byline: "What The Rich Teach Their Kids About Money.",
  description:
    "Rich isn\u2019t a paycheck \u2014 it\u2019s a decision, then a system. Kiyosaki\u2019s \u2018rich dad\u2019 refused to trade hours for dollars and built assets that paid him whether he showed up or not. SHYFT Coalition\u2122 is built on that same rule: teach money to work for you, and freedom stops being a fantasy.",
  month_label: "September 2026 \u2014 Book of the Month",
};

export const BookOfTheMonth = () => {
  const [books, setBooks] = useState([]);
  const [currentSlug, setCurrentSlug] = useState(FALLBACK_BOOK.slug);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;
    fetch(`${base}/api/books`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        setBooks(data.books || []);
        setCurrentSlug(data.current || "");
      })
      .catch(() => {});
  }, []);

  const currentBook =
    books.find((b) => b.slug === currentSlug) || FALLBACK_BOOK;
  const pastEditions = books.filter((b) => b.slug !== currentSlug);

  const openShare = (book) => {
    setShareTarget(book);
    setShareOpen(true);
  };

  const shareUrl = shareTarget ? buildBookShareUrl(shareTarget.slug) : "";
  const shareText = shareTarget
    ? `${shareTarget.title} \u2014 SHYFT Book of the Month. ${shareTarget.byline || ""}`.trim()
    : "";

  return (
    <section
      id="book"
      data-testid="book-section"
      className="relative py-10 md:py-14"
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="rounded-[1.75rem] md:rounded-[2rem] border border-[rgba(201,162,91,0.22)] bg-gradient-to-br from-[#1c1814] via-[#14110e] to-[#0a0a0a] overflow-hidden">
          <div className="grid lg:grid-cols-12 gap-0 items-stretch">
            {/* Book visual */}
            <div className="lg:col-span-5 relative min-h-[300px] md:min-h-[360px] reveal">
              <div
                aria-hidden
                className="absolute inset-0"
                style={{
                  background:
                    "radial-gradient(60% 60% at 50% 50%, rgba(231,201,138,0.22), transparent 70%)",
                }}
              />
              <img
                src={IMAGES.bookOfTheMonth}
                alt={`${currentBook.title} — Book of the Month`}
                data-testid="book-image"
                className="absolute inset-0 w-full h-full object-contain p-4 md:p-6 float-slow"
                loading="lazy"
              />
              <div className="absolute top-4 left-4 px-2.5 py-1 rounded-full bg-[#0a0a0a]/70 border border-[rgba(201,162,91,0.4)] backdrop-blur">
                <span className="eyebrow !text-[9px] !text-[#e7c98a]">
                  Monthly · Selection
                </span>
              </div>
            </div>

            {/* Copy */}
            <div className="lg:col-span-7 p-6 md:p-8 lg:p-10 flex flex-col justify-center reveal">
              {/* Gold month pill */}
              <div className="inline-flex self-start items-center px-3 py-1 rounded-md bg-gradient-to-r from-[#e7c98a] to-[#c9a25b] shadow-[0_4px_18px_-6px_rgba(231,201,138,0.5)]">
                <span
                  data-testid="book-month-label"
                  className="text-[#1a140d] text-[10px] md:text-[11px] font-bold tracking-[0.18em] uppercase"
                >
                  {currentBook.month_label}
                </span>
              </div>

              <h2
                data-testid="book-title"
                className="font-serif-display mt-3 text-xl md:text-2xl lg:text-[1.75rem] leading-[1.1] text-white"
              >
                {currentBook.title}
              </h2>

              {currentBook.author ? (
                <p
                  data-testid="book-author"
                  className="mt-1.5 text-base md:text-lg text-gradient-gold font-serif-display"
                >
                  {currentBook.author}
                </p>
              ) : null}

              {currentBook.byline ? (
                <p className="mt-3 italic text-[#cfc8b9] text-sm md:text-[15px] leading-relaxed">
                  {currentBook.byline}
                </p>
              ) : null}

              {currentBook.description ? (
                <p
                  data-testid="book-description"
                  className="mt-3 max-w-xl text-[#b8b3aa] text-sm md:text-[14.5px] leading-relaxed"
                >
                  {currentBook.description}
                </p>
              ) : null}

              {/* Meta pills — Monthly Discussion is the only clickable one */}
              <div className="mt-5 grid sm:grid-cols-3 gap-2">
                {[
                  {
                    Icon: BookOpen,
                    k: "12 Books",
                    v: "per year",
                    testid: "book-pill-12-books",
                  },
                  {
                    Icon: Feather,
                    k: "Monthly Discussion",
                    v: "RSVP \u2192 Last Wednesday",
                    href: LINKS.bookDiscussionRsvp,
                    testid: "book-pill-discussion",
                  },
                  {
                    Icon: Library,
                    k: "Archive Library",
                    v: "of success books",
                    testid: "book-pill-archive",
                  },
                ].map(({ Icon, k, v, href, testid }) => {
                  const inner = (
                    <>
                      <Icon size={16} className="text-[#6fb3ff]" />
                      <div className="leading-tight text-left">
                        <div className="text-white text-sm">{k}</div>
                        <div className="eyebrow !text-[9px] !text-[#b8b3aa]">
                          {v}
                        </div>
                      </div>
                    </>
                  );
                  const classes =
                    "flex items-center gap-2.5 rounded-full border border-[rgba(30,144,255,0.45)] bg-[#0a0a0a]/60 px-3 py-2 transition hover:border-[#1e90ff] hover:shadow-[0_0_18px_-4px_rgba(30,144,255,0.6)]";
                  return href ? (
                    <a
                      key={k}
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={testid}
                      className={classes}
                    >
                      {inner}
                    </a>
                  ) : (
                    <div key={k} data-testid={testid} className={classes}>
                      {inner}
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={LINKS.bookOfTheMonth}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="book-buy-cta"
                  className="btn-gold !py-3 !px-6 !text-sm"
                >
                  Acquire This Edition
                  <ArrowUpRight size={16} strokeWidth={2.4} />
                </a>
                <a
                  href={LINKS.books}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="book-browse-cta"
                  className="btn-outline-gold !py-2.5 !px-5 !text-sm"
                >
                  Browse Print Library
                </a>
                <button
                  type="button"
                  onClick={() => openShare(currentBook)}
                  data-testid="book-share-cta"
                  className="btn-outline-gold !py-2.5 !px-5 !text-sm"
                >
                  <Share2 size={13} strokeWidth={2.2} />
                  Share this Book
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Past Editions rail ─────────────────────────────── */}
        {pastEditions.length > 0 ? (
          <div className="reveal mt-14 md:mt-16">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
              <div>
                <div className="eyebrow !text-[#6fb3ff]">
                  SHYFT Codex Library
                </div>
                <h3 className="font-serif-display mt-2 text-2xl md:text-[1.75rem] text-white">
                  Past <span className="text-gradient-gold">Editions</span>
                </h3>
              </div>
              <p className="text-[#80786c] text-xs tracking-wide">
                {pastEditions.length} previous{" "}
                {pastEditions.length === 1 ? "edition" : "editions"} ·
                shareable individually
              </p>
            </div>

            <div
              data-testid="past-editions-rail"
              className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scroll-px-6 -mx-2 px-2"
              style={{ scrollbarWidth: "thin" }}
            >
              {pastEditions.map((b) => (
                <article
                  key={b.slug}
                  data-testid={`past-edition-${b.slug}`}
                  className="snap-start shrink-0 w-[280px] md:w-[320px] rounded-[1.5rem] border border-[rgba(201,162,91,0.22)] bg-[#14110e]/70 backdrop-blur overflow-hidden group transition hover:border-[rgba(30,144,255,0.55)] hover:shadow-[0_0_24px_-8px_rgba(30,144,255,0.5)]"
                >
                  <a
                    href={`?book=${b.slug}#book`}
                    className="block aspect-[1200/630] relative overflow-hidden"
                  >
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/og-card?slug=${b.slug}`}
                      alt={b.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition duration-700 group-hover:scale-[1.03]"
                    />
                  </a>
                  <div className="p-4 flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="eyebrow !text-[9px] !text-[#80786c] truncate">
                        {b.month_label}
                      </div>
                      <h4 className="font-serif-display text-white text-base leading-tight mt-1 truncate">
                        {b.title}
                      </h4>
                      {b.byline ? (
                        <p className="text-[#b8b3aa] text-[12px] leading-snug mt-1 line-clamp-2">
                          {b.byline}
                        </p>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      onClick={() => openShare(b)}
                      data-testid={`past-edition-${b.slug}-share`}
                      aria-label={`Share ${b.title}`}
                      className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full border border-[rgba(201,162,91,0.3)] text-gold hover:text-[#6fb3ff] hover:border-[rgba(30,144,255,0.55)] transition"
                    >
                      <Share2 size={14} />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        url={shareUrl}
        text={shareText}
        title={
          shareTarget ? (
            <>
              Share <span className="text-gradient-gold">{shareTarget.title}</span>
            </>
          ) : null
        }
        description={
          shareTarget?.byline ||
          "Pass this edition to someone who needs to SHYFT how they think."
        }
      />
    </section>
  );
};

export default BookOfTheMonth;
